/**
 * PATH: packages/ui/src/kanban/lane-count.ts
 * FILE_ID: UI-KANBAN-LANECOUNT-001
 * PURPOSE: Single source of truth for "how many (active) lanes does this
 *          board have?" across the rail, gallery, and duplicates dialog.
 * WHY:
 *   PERF-2026-06-14-KANBAN-LANE-COUNT. The boards LIST endpoint
 *   (GET /api/kanban/boards/) historically embedded every lane of every
 *   board, but the list surfaces only ever reduce that array to ONE
 *   integer per board (the non-archived lane badge + the duplicate-group
 *   "richness" ranking). On the worst tenant that is ~6000 lane objects
 *   serialised + shipped purely to derive a count (live p95 ~1.56s).
 *
 *   The backend now exposes `active_lane_count` / `lane_count` directly
 *   (EXPAND step). These helpers prefer the server-computed count and
 *   fall back to counting the embedded `lanes` array, so the FE works
 *   against BOTH the old payload (lanes embedded) and the post-CONTRACT
 *   payload (lanes dropped from the list). Once every client reads these
 *   helpers, the backend stops embedding `lanes` in the list response.
 *
 * FLOW:
 *   ┌──────────────────────────────┐   ┌───────────────────┐   ┌───────┐
 *   │ Board (active_lane_count? +  │ → │ prefer server count│ → │ number│
 *   │ lanes? from API)             │   │ else count lanes[] │   │       │
 *   └──────────────────────────────┘   └───────────────────┘   └───────┘
 */

import type { Board } from './types'

type LaneCountable = Pick<Board, 'active_lane_count' | 'lanes'>
type TotalCountable = Pick<Board, 'lane_count' | 'lanes'>

/**
 * Non-archived lane count for a board. Prefers the server-computed
 * `active_lane_count`; falls back to counting the embedded `lanes`.
 */
export function getActiveLaneCount(board: LaneCountable): number {
  if (typeof board.active_lane_count === 'number') return board.active_lane_count
  const lanes = board.lanes
  if (!Array.isArray(lanes)) return 0
  let n = 0
  for (const l of lanes) {
    if (!l.archived) n++
  }
  return n
}

/**
 * Total lane count for a board (archived + active). Prefers the
 * server-computed `lane_count`; falls back to the embedded `lanes` length.
 */
export function getTotalLaneCount(board: TotalCountable): number {
  if (typeof board.lane_count === 'number') return board.lane_count
  return Array.isArray(board.lanes) ? board.lanes.length : 0
}
